import { useEffect } from 'react';
import { FLAGS } from '@/config/featureFlags';
import { reportError } from '@/services/errorTracking';

/**
 * Plan 3.2: SplashScreen.launchAutoHide is off so the dark launch frame stays
 * up until the app has actually painted, avoiding the white-flash gap. The
 * landing page does not mount the redesign Shell, so the hide call lives at
 * the app root instead. Two rAFs guarantee a real paint before hide(); a 3s
 * timeout is the fallback for the launchAutoHide:false hang regression
 * (capacitor-plugins#446). N1: the plugin is only ever imported inside the
 * FLAGS.nativeIOS branch, and never awaited/returned as the Proxy itself —
 * only its cached `mod.SplashScreen.hide()` call is awaited.
 */
export function NativeSplashHide(): null {
  useEffect(() => {
    if (!FLAGS.nativeIOS) return;

    let hidden = false;
    let cancelled = false;

    const hide = (): void => {
      if (hidden || cancelled) return;
      hidden = true;
      void import('@capacitor/splash-screen')
        .then((mod) => mod.SplashScreen.hide())
        .catch((error: unknown) => {
          reportError(error, { operation: 'NativeSplashHide.hide' });
        });
    };

    const timeoutId = window.setTimeout(hide, 3000);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        hide();
      });
    });

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, []);

  return null;
}

export default NativeSplashHide;
